import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from '../contexts/AuthContext';
import { toast } from "sonner";
import FullPageLoader from './FullPageLoader';
import { useHealthCheck } from '../hooks/useHealthCheck';

const Login = () => {
  const { login } = useAuth();
  const { isHealthy, isLoading: healthLoading, error: healthError } = useHealthCheck();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [systemSettings, setSystemSettings] = useState({
    system_name: 'DK Farms',
    logo_url: 'https://customer-assets.emergentagent.com/job_farmflow-system-2/artifacts/1e5nqcxy_LOGO%20new.jpg'
  });

  useEffect(() => {
    // Fetch system settings on component mount (public endpoint, no authentication needed)
    const fetchSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${API_URL}/api/system/settings`);
        setSystemSettings(response.data);
        console.log("✅ System settings loaded:", response.data);
      } catch (error) {
        console.log("⚠️ Using default system settings", error?.message);
        // Keep using default settings from state
      }
    };
    fetchSettings();
  }, []);

  // Show full page loader if API is not healthy
  if (healthLoading || !isHealthy) {
    const message = healthError || 'API is starting up...';
    return <FullPageLoader message={message} />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success('Login successful!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        <div className="logo-section">
          <img 
            src={systemSettings.logo_url} 
            alt={`${systemSettings.system_name} Logo`} 
            className="farm-logo"
          />
        </div>
        
        <Card className="login-card">
          <CardHeader>
            <CardTitle>FARM MANAGEMENT</CardTitle>
            <CardDescription>Login to manage {systemSettings.system_name}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError('');
                  }}
                  required
                  disabled={loading}
                  data-testid="username-input"
                  placeholder="Enter your username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  required
                  disabled={loading}
                  data-testid="password-input"
                  placeholder="Enter your password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
